import { useCallback, useEffect, useState } from "react";
import i18n, { LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES, type LanguageCode } from "../i18n";

/** Mirrors useTheme.ts's shape: localStorage-first, single setter as the only writer. */
export default function useLanguage() {
  const [language, setLanguageState] = useState<LanguageCode>(i18n.language as LanguageCode);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => setLanguageState(lng as LanguageCode);
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);

  // WCAG 3.1.1 (Language of Page) — assistive tech relies on this attribute
  // for pronunciation rules; index.html hardcodes lang="en" for the initial
  // paint, so this is what keeps it correct after a language switch.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: LanguageCode) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    void i18n.changeLanguage(next);
  }, []);

  return { language, setLanguage, supportedLanguages: SUPPORTED_LANGUAGES };
}
